/**
 * <TutorStudentReportsPage> — l'onglet Rapports de la fiche d'un élève (page).
 *
 * La route `live-reports` (`LiveReportsScreen.js`, branche hors mode parent), sous l'en-tête de la
 * fiche. La liste du parent (`<LiveReportsListPage>`), dont chaque carte porte la date, les chips, la
 * capsule du tuteur, l'anneau d'engagement et le commentaire, avec le diagnostic en tête
 * (`<TutorStudentDiagnosticCard>`, qui était une modale).
 *
 * Un rapport complet s'ouvre ici (`openReport`), avec « Tous ses rapports », au lieu d'un écran poussé.
 *
 * La colonne « Satisfaction élève » du tableau n'était remplie que pour l'admin : elle ne revient pas.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { useLiveReportCardWidth } from "../../organisms/LiveReportCard/LiveReportCard"
import {
    TutorStudentDiagnosticCard,
    type TutorStudentDiagnosticCardProps,
} from "../../organisms/TutorStudentDiagnosticCard"
import { LiveReportDetailsTemplate } from "../../templates/LiveReportDetailsTemplate"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { LiveReportDetailsSkeleton } from "../LiveReportDetailsPage/LiveReportDetailsSkeleton"
import { LiveReportsListPage, type LiveReportsListPageProps } from "../LiveReportsListPage/LiveReportsListPage"
import { type UILiveSessionReportDetails } from "../../../ports/primary/ui/types"

export type TutorStudentReportsPageProps = Omit<LiveReportsListPageProps, "listHeader"> & {
    /** Le diagnostic, en tête de la liste. Absent : pas de bloc. */
    diagnostic?: TutorStudentDiagnosticCardProps
    /** Un rapport ouvert dans l'onglet (`report` nul tant qu'il charge). Absent : la liste. */
    openReport?: {
        report: UILiveSessionReportDetails | null
        loading?: boolean
        error?: unknown
        onRetry?: () => void
    } | null
    onCloseReport?: () => void
}

export function TutorStudentReportsPage(props: TutorStudentReportsPageProps) {
    const { diagnostic, openReport, onCloseReport, ...list } = props
    const cardWidth = useLiveReportCardWidth()

    if (openReport != null) {
        return (
            <PageTemplate
                loading={openReport.loading}
                skeleton={<LiveReportDetailsSkeleton />}
                error={openReport.error}
                onRetry={openReport.onRetry}
                padding="4"
                maxContentWidth={PARENT_CONTENT_WIDTH}
                header={
                    <Row style={styles.back}>
                        <Button
                            label="Tous ses rapports"
                            iconName="chevron-left"
                            variant="ghost"
                            color="primary"
                            size="sm"
                            onPress={onCloseReport}
                        />
                    </Row>
                }
            >
                {openReport.report != null ? <LiveReportDetailsTemplate report={openReport.report} /> : null}
            </PageTemplate>
        )
    }

    return (
        <LiveReportsListPage
            {...list}
            listHeader={
                diagnostic != null ? (
                    <View style={styles.diagnostic(cardWidth)}>
                        <TutorStudentDiagnosticCard {...diagnostic} />
                    </View>
                ) : undefined
            }
        />
    )
}

const styles = StyleSheet.create((theme) => ({
    back: { paddingHorizontal: theme.spacing["4"], paddingTop: theme.spacing["2"] },
    /** À la largeur des cartes de rapport, centré comme elles. */
    diagnostic: (width: number) => ({ width, alignSelf: "center" as const, paddingBottom: theme.spacing["3"] }),
}))
